/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import {
  PhoneIncoming,
  PhoneOutgoing,
  Mail,
  MessageSquareReply,
  Send,
  Calendar,
  Globe,
  Search,
  Home,
  UserPlus,
  Heart,
  StickyNote,
} from 'lucide-react';
import { formatTemplateName } from '../formatters/templateFormatters';
import { generateEmailTemplateUrl } from '../email/generateEmailTemplateUrl';
import { previewEmail } from '../email/previewEmail';
import { useOrganizationStore } from '../../stores/useOrganizationStore';
import { getFlowTypeStyle, formatOutcome } from '../badgeStyles';
import { OutcomeBadge } from '../../components/common/OutcomeBadge';

// Re-export these for use in other components
export { getFlowTypeStyle, formatOutcome };
import { Activity } from '../../types/activity.types';

// Helper function to format date as a group header
export const formatDateGroup = (date: Date): string => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Check if it's today
  if (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  ) {
    return 'Today';
  }

  // Check if it's yesterday
  if (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  ) {
    return 'Yesterday';
  }

  // For all other dates, show month and day
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

// Group activities by date
export const groupActivitiesByDate = (activities: Activity[]) => {
  const groups: Record<string, Activity[]> = {};

  activities.forEach((activity) => {
    const dateGroup = formatDateGroup(activity.timestamp);
    if (!groups[dateGroup]) {
      groups[dateGroup] = [];
    }
    groups[dateGroup].push(activity);
  });

  // Sort the groups by date (most recent first)
  return Object.entries(groups).sort((a, b) => {
    // Get the first activity in each group to compare dates
    const dateA = a[1][0].timestamp.getTime();
    const dateB = b[1][0].timestamp.getTime();
    return dateB - dateA;
  });
};

// Helper function to get email event type display name
export const getEmailEventTypeDisplay = (eventType: string): string => {
  switch (eventType.toLowerCase()) {
    case 'outbound':
    case 'sent':
    case 'queued':
    case 'processed':
      return 'Email Sent';
    case 'delivered':
      return 'Email Delivered';
    case 'open':
    case 'opened':
      return 'Email Opened';
    case 'click':
    case 'clicked':
      return 'Email Clicked';
    case 'bounce':
    case 'bounced':
      return 'Email Bounced';
    case 'failed':
      return 'Email Failed';
    case 'dropped':
      return 'Email Dropped';
    case 'blocked':
      return 'Email Blocked';
    case 'deferred':
      return 'Email Deferred';
    case 'spamreport':
      return 'Reported as Spam';
    case 'unsubscribe':
    case 'unsubscribed':
      return 'Unsubscribed';
    case 'received':
    case 'inbound':
      return 'Email Received';
    default:
      return `Email ${eventType.charAt(0).toUpperCase() + eventType.slice(1).toLowerCase()}`;
  }
};

// Helper function to get email description
export const getEmailDescription = (email: any): React.ReactNode => {
  const subject = email.subjectLine || email.subject || email.details?.subject_line || 'No subject';
  const template = email.templateName || email.details?.template_name;

  if (!template) {
    return subject;
  }

  // Get organization from context
  const { organization } = useOrganizationStore.getState();

  if (!organization) {
    return `${formatTemplateName(template)} - ${subject}`;
  }

  const displayText = `${formatTemplateName(template)} - ${subject}`;

  // Check if this is a scheduled eAlert email
  const isScheduledEAlert =
    (!email.event_type || email.event_type === 'scheduled') &&
    template === 'e_alert_with_properties';

  if (isScheduledEAlert) {
    // For scheduled eAlert emails, show a clickable button that opens the sample template
    return (
      <button
        onClick={() => previewEmail('', true)}
        className="underline text-left hover:text-primary"
      >
        {displayText}
      </button>
    );
  }

  // If this is an eAlert email with bodyHtml, create a clickable button that opens preview
  if (email.bodyHtml) {
    return (
      <button
        onClick={() => previewEmail(email.bodyHtml)}
        className="underline text-left hover:text-primary"
      >
        {displayText}
      </button>
    );
  }

  // Generate URL for regular email templates
  const templateUrl = generateEmailTemplateUrl(template, organization, undefined, subject);

  return (
    <a href={templateUrl} target="_blank" rel="noopener noreferrer" className="underline">
      {displayText}
    </a>
  );
};

// Helper function to get call description
export const getCallDescription = (call: any): React.ReactNode => {
  const parts: React.ReactNode[] = [];

  // Add answered by info
  if (call.answered_by) {
    if (call.answered_by === 'human') {
      parts.push('Answered by human');
    } else if (call.answered_by === 'voicemail') {
      parts.push('Went to voicemail');
    } else if (call.answered_by === 'no answer') {
      parts.push('No answer');
    } else {
      parts.push(`Answered by ${call.answered_by}`);
    }
  }

  // Add flow name if available with link
  if (call.flowExecution?.flow_name && call.flowExecution?.flow_id) {
    const flowName = call.flowExecution.flow_name;
    const flowId = call.flowExecution.flow_id;

    // Create a link to the flow
    parts.push(
      <>
        <a href={`/flows/${flowId}`} className="underline">
          {flowName}
        </a>
      </>
    );
  } else if (call.flowExecution?.flow_name) {
    parts.push(`Flow: ${call.flowExecution.flow_name}`);
  }

  // Add call outcome if available with badge
  if (call.call_analysis?.outcome) {
    const outcome = call.call_analysis.outcome;
    parts.push(<OutcomeBadge outcome={outcome} />);
  }

  if (parts.length === 0) {
    return 'Call details not available';
  }

  // Join the parts with separators
  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 && ' - '}
          {part}
        </React.Fragment>
      ))}
    </>
  );
};

// Helper function to get icon style based on activity type
export const getIconStyle = (
  type: 'email' | 'call' | 'website' | 'note'
): { background: string; text: string } => {
  switch (type) {
    case 'email':
      return { background: 'action-icon-blue', text: '' };
    case 'call':
      return { background: 'action-icon-purple', text: '' };
    case 'website':
      return { background: 'action-icon-green', text: '' };
    case 'note':
      return { background: 'action-icon-yellow', text: '' };
    default:
      return { background: 'bg-muted', text: 'text-muted-foreground' };
  }
};

// Helper function to format scheduled date
export const formatScheduledDate = (
  scheduledTime: string | { seconds: number; nanoseconds: number }
) => {
  try {
    if (typeof scheduledTime === 'string') {
      return new Date(scheduledTime);
    } else if (scheduledTime && 'seconds' in scheduledTime) {
      return new Date(scheduledTime.seconds * 1000);
    }
    return new Date();
  } catch (error) {
    console.error('Error parsing scheduled date:', error);
    return new Date();
  }
};

// Helper function to get flow info for call details panel
export const getFlowInfo = (call: any) => {
  if (!call?.flowExecution) {
    return { name: '-', type: '-' };
  }

  return {
    name: call.flowExecution.flow_name || '-',
    type: call.flowExecution.flow_type || '-',
  };
};

// Helper function to get activity icon
/**
 * Shared icon resolver for activity items — used by both ActivityItem and RecentActivities.
 * Uses CSS utility classes for proper dark mode support.
 * @param size - icon size class, defaults to 'h-4 w-4'
 */
export const getActivityIcon = (activity: Activity, size = 'h-4 w-4') => {
  if (activity.type === 'call') {
    const raw = activity.sourceData?.metadata;
    let metaDir: string | undefined;
    try {
      if (typeof raw === 'string') metaDir = JSON.parse(raw)?.direction;
      else if (raw && typeof raw === 'object') metaDir = (raw as Record<string, unknown>).direction as string;
    } catch { /* ignore */ }
    return metaDir === 'inbound' ? (
      <PhoneIncoming className={`${size} call-color-inbound`} />
    ) : (
      <PhoneOutgoing className={`${size} call-color-outbound`} />
    );
  }

  if (activity.type === 'email') {
    return <Mail className={`${size} ealert-color`} />;
  }

  if (activity.type === 'sms') {
    // SMS gets its own warm-tone palette so the channel doesn't read as
    // a call (blue/green) or email (violet) at a glance:
    //   inbound  → MessageSquareReply, amber
    //   outbound → Send, rose
    // Shape conveys direction; colour family says "SMS".
    const sd = activity.sourceData as Record<string, unknown> | undefined;
    let dir = (sd?.direction ?? sd?.event_subtype ?? sd?.eventSubtype) as string | undefined;
    if (!dir) {
      const raw = sd?.metadata;
      try {
        if (typeof raw === 'string') dir = JSON.parse(raw)?.direction;
        else if (raw && typeof raw === 'object') dir = (raw as Record<string, unknown>).direction as string;
      } catch { /* ignore */ }
    }
    return dir === 'inbound' ? (
      <MessageSquareReply className={`${size} text-amber-600 dark:text-amber-400`} />
    ) : (
      <Send className={`${size} text-rose-600 dark:text-rose-400`} />
    );
  }

  if (activity.type === 'note') {
    return <StickyNote className={`${size} text-yellow-700 dark:text-yellow-400`} />;
  }

  if (activity.type === 'website') {
    const eventType = activity.sourceData?.event_type;

    switch (eventType) {
      case 'property_view': {
        const evtCat = activity.sourceData?.event_category || '';
        const pageUrl = activity.sourceData?.page_url || '';
        const isSeller = evtCat === 'Valuation' || pageUrl?.includes('valuation');
        return (
          <Home
            className={`${size} ${isSeller ? 'text-teal-700 dark:text-teal-300' : 'text-[#9D174D] dark:text-pink-400'}`}
          />
        );
      }
      case 'property_saved':
        return <Heart className={`${size} text-[#9D174D] dark:text-pink-400`} />;
      case 'search':
        return <Search className={`${size} text-[#92400E] dark:text-yellow-400`} />;
      case 'lead_capture':
        return <UserPlus className={`${size} text-red-700 dark:text-red-400`} />;
      default:
        return <Globe className={`${size} text-muted-foreground`} />;
    }
  }

  // scheduled_call or email_campaign
  return <Calendar className={`${size} text-purple-700 dark:text-purple-400`} />;
};

/** Icon background class for activity types — matches the icon colors */
export const getActivityIconBg = (activity: {
  type: string;
  sourceData?: Record<string, any>;
}): string => {
  if (activity.type === 'call') {
    const raw = activity.sourceData?.metadata;
    let metaDir: string | undefined;
    try {
      if (typeof raw === 'string') metaDir = JSON.parse(raw)?.direction;
      else if (raw && typeof raw === 'object') metaDir = (raw as Record<string, unknown>).direction as string;
    } catch { /* ignore */ }
    return metaDir === 'inbound' ? 'call-icon-inbound' : 'call-icon-outbound';
  }
  if (activity.type === 'email') return 'ealert-icon-bg';
  if (activity.type === 'sms') {
    // Warm-tone pair matching the SMS icon colours above — amber for
    // inbound, rose for outbound. Keeps the channel visually distinct
    // from calls (blue/green) and email (violet) at a glance.
    const sd = activity.sourceData as Record<string, unknown> | undefined;
    let dir = (sd?.direction ?? sd?.event_subtype ?? sd?.eventSubtype) as string | undefined;
    if (!dir) {
      const raw = sd?.metadata;
      try {
        if (typeof raw === 'string') dir = JSON.parse(raw)?.direction;
        else if (raw && typeof raw === 'object') dir = (raw as Record<string, unknown>).direction as string;
      } catch { /* ignore */ }
    }
    return dir === 'inbound'
      ? 'bg-amber-100 dark:bg-amber-500/15'
      : 'bg-rose-100 dark:bg-rose-500/15';
  }
  if (activity.type === 'note') return 'action-icon-yellow';
  if (activity.type === 'scheduled_call') return 'call-icon-scheduled';
  if (activity.type === 'website') {
    const eventType = activity.sourceData?.event_type;
    if (eventType === 'property_view' || eventType === 'property_saved') {
      const evtCat = activity.sourceData?.event_category || '';
      const pageUrl = activity.sourceData?.page_url || '';
      if (evtCat === 'Valuation' || pageUrl?.includes('valuation'))
        return 'web-evt-property-seller';
      return 'web-evt-property-buyer';
    }
    if (eventType === 'search') return 'web-evt-search';
    if (eventType === 'lead_capture') return 'web-evt-lead';
    return 'bg-slate-100 dark:bg-slate-900/20';
  }
  return 'bg-slate-100 dark:bg-slate-900/20';
};
